import {
  RestartSubscriptionModel,
  SubscriptionModel,
  SubscriptionResponse,
  SubscriptionResponseStatus,
  SubscriptionSource,
  SubscriptionStatus,
  SubscriptionUpsellDowngradeListItem,
  SubscriptionUpsellDowngradeListResponse,
  PauseHistoryItem,
  PauseHistoryResponse,
} from "../model/subscriptions";
import { PreviewPurchaseResponse } from "../dto/response/purchase";

export function transformSubscriptions(dto: SubscriptionResponse): SubscriptionModel {
  const subscriptionModel: SubscriptionModel = {
    title: dto.title,
    status: getStatus(dto),
    paymentMethod: dto.payment_method
      ? {
          cardType: dto.payment_method.card_type,
          account: dto.payment_method.account,
          billingAddress: {
            country: dto.payment_method.billing_address.country,
          },
        }
      : undefined,
    source: dto.source,
    subscriptionId: dto.subscription_id,
    startDate: dto.start_date,
    endDate: dto.next_billing?.date ? undefined : dto.end_date,
    pauseStartDate: dto.paused_start_date,
    cancelDate: dto.cancel_date,
    cancellationSource: dto.cancellation_source,
    pid: dto.pid,
    productId: dto.product_id,
    actions: [],
    pauseCount: dto.pause_count,
  };

  if (dto.grace_period_end_date) {
    subscriptionModel.endDate = dto.grace_period_end_date;
  }

  if (dto.next_billing) {
    subscriptionModel.nextBilling = {
      date: dto.next_billing.date,
      amount: dto.next_billing.amount,
      currencySymbol: dto.next_billing.currency_symbol,
    };
  }

  subscriptionModel.actions = getActionsByStatusAndSource(subscriptionModel.status, subscriptionModel.source);

  return subscriptionModel;
}

const getStatus = (dto: SubscriptionResponse): SubscriptionStatus => {
  if (dto.grace_period_end_date) {
    return SubscriptionStatus.GRACE_PERIOD;
  }
  if (dto.free_trial_end_date) {
    return SubscriptionStatus.FREE_TRIAL;
  }
  if (dto.status === SubscriptionResponseStatus.CANCELLED) {
    return SubscriptionStatus.CANCELLED;
  }
  if (dto.status === SubscriptionResponseStatus.ACTIVE) {
    return SubscriptionStatus.ACTIVE;
  }
  if (dto.status === SubscriptionResponseStatus.PENDING_CANCEL) {
    return SubscriptionStatus.PENDING_CANCEL;
  }
  if (dto.status === SubscriptionResponseStatus.PENDING_PAUSE) {
    return SubscriptionStatus.PENDING_PAUSE;
  }
  if (dto.status === SubscriptionResponseStatus.PENDING_ACTIVATION) {
    return SubscriptionStatus.PENDING_ACTIVATION;
  }
  if (dto.status === SubscriptionResponseStatus.PAUSED) {
    return SubscriptionStatus.PAUSED;
  }
  if (dto.status.toUpperCase() === SubscriptionResponseStatus.Expired.toUpperCase()) {
    return SubscriptionStatus.Expired;
  }
  return SubscriptionStatus.UNKNOWN;
};

const getActionsByStatusAndSource = (status: string, source: SubscriptionSource): string[] => {
  const statusActionsMapping: {
    [status: string]: { [source: string]: string[] };
  } = {
    [SubscriptionStatus.ACTIVE]: {
      [SubscriptionSource.eCom]: [],
      [SubscriptionSource.RECURLY]: [
        "extendBillingDate",
        "applyPromoCode",
        "cancel",
        "revokeEntitlement",
        "pause",
        "upsell",
        "downgrade",
        "switchPlan",
      ],
      [SubscriptionSource.Naviga]: [],
    },
    [SubscriptionStatus.FREE_TRIAL]: {
      [SubscriptionSource.eCom]: [],
      [SubscriptionSource.RECURLY]: [
        "extendBillingDate",
        "applyPromoCode",
        "cancel",
        "revokeEntitlement",
        "upsell",
        "downgrade",
        "switchPlan",
      ],
      [SubscriptionSource.Naviga]: [],
    },
    [SubscriptionStatus.PENDING_CANCEL]: {
      [SubscriptionSource.eCom]: [],
      [SubscriptionSource.RECURLY]: ["revokeEntitlement"],
      [SubscriptionSource.Naviga]: [],
    },
    [SubscriptionStatus.CANCELLED]: {
      [SubscriptionSource.eCom]: [],
      [SubscriptionSource.RECURLY]: ["restart"],
      [SubscriptionSource.Naviga]: [],
    },
    [SubscriptionStatus.Expired]: {
      [SubscriptionSource.eCom]: [],
      [SubscriptionSource.RECURLY]: ["restart"],
      [SubscriptionSource.Naviga]: [],
    },
    [SubscriptionStatus.PENDING_PAUSE]: {
      [SubscriptionSource.eCom]: [],
      [SubscriptionSource.RECURLY]: ["cancel", "resume", "revokeEntitlement"],
      [SubscriptionSource.Naviga]: [],
    },
    [SubscriptionStatus.PAUSED]: {
      [SubscriptionSource.eCom]: [],
      [SubscriptionSource.RECURLY]: ["resume", "cancel", "revokeEntitlement"],
      [SubscriptionSource.Naviga]: [],
    },
    [SubscriptionStatus.GRACE_PERIOD]: {
      [SubscriptionSource.eCom]: [],
      [SubscriptionSource.RECURLY]: ["cancel", "revokeEntitlement"],
      [SubscriptionSource.Naviga]: [],
    },
  };

  if (statusActionsMapping[status] && statusActionsMapping[status][source]) {
    return statusActionsMapping[status][source];
  } else {
    return [];
  }
};

export function transformUpsellDowngradeItem(
  dto: SubscriptionUpsellDowngradeListResponse
): SubscriptionUpsellDowngradeListItem {
  return {
    pid: dto.pid,
    planTitle: dto.plan_title,
    basePrice: {
      id: dto.base_price.id,
      name: dto.base_price.name,
    },
    billingPlan: dto.billing_plan,
  };
}

export function transformRestartSubscription(response: PreviewPurchaseResponse): RestartSubscriptionModel {
  return {
    planTitle: response.product_plan.title,
    billingPlanName: response.order_summary.billing_plan?.name || "",
    symbol: response.order_summary.currency.symbol,
    amount: response.order_summary.original_price,
    tax: response.order_summary.tax,
    total: response.order_summary.total,
    paymentMethod: response.payment_method && {
      ...response.payment_method,
      billingAddress: response.payment_method.billing_address && {
        ...response.payment_method.billing_address,
        postalCode: response.payment_method.billing_address.postal_code,
      },
      expirationDate: response.payment_method.expiration_date,
      cardType: response.payment_method.card_type,
    },
  };
}

export function transformPauseHistoryItem(dto: PauseHistoryResponse): PauseHistoryItem {
  return {
    createdAt: dto.created_at,
    pausedFrom: dto.paused_from,
    pauseUntil: dto.pause_until,
    resumedAt: dto.resumed_at,
    createdBy: dto.created_by,
    resumedBy: dto.resumed_by,
    status: dto.status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
  };
}
